// Mirrors app/lib/core/constants/category_icon_data.dart. Keep the two
// maps in sync when the Flutter side gains new icons — the emoji key
// on the server row is the join key.
export interface CategoryIconMeta {
  iconPath: string;
  color: string;
  label: string;
}

const FALLBACK: CategoryIconMeta = {
  iconPath: "/icons/dots-three.svg",
  color: "#888888",
  label: "Other",
};

// AppColors.dart values mirrored to hex so the admin dark UI matches
// what the Flutter app renders.
const COLORS = {
  accent: "#34D399",
  purple: "#818CF8",
  amber: "#FBBF24",
  red: "#F87171",
  blue: "#60A5FA",
  pink: "#F472B6",
  teal: "#2DD4BF",
  orange: "#FB923C",
  violet: "#8B5CF6",
  zinc: "#888888",
} as const;

export const CATEGORY_ICONS: Record<string, CategoryIconMeta> = {
  cart: { iconPath: "/icons/shopping-cart.svg", color: COLORS.accent, label: "Groceries" },
  bulb: { iconPath: "/icons/lightning.svg", color: COLORS.purple, label: "Bills" },
  hospital: { iconPath: "/icons/heartbeat.svg", color: COLORS.blue, label: "Medical" },
  car: { iconPath: "/icons/bus.svg", color: COLORS.amber, label: "Transport" },
  book: { iconPath: "/icons/graduation-cap.svg", color: COLORS.teal, label: "Education" },
  fork_knife: { iconPath: "/icons/fork-knife.svg", color: COLORS.red, label: "Food" },
  film: { iconPath: "/icons/play-circle.svg", color: COLORS.pink, label: "Entertainment" },
  bank: { iconPath: "/icons/bank.svg", color: COLORS.purple, label: "EMI/Bank" },
  house: { iconPath: "/icons/house-simple.svg", color: COLORS.violet, label: "Rent" },
  broom: { iconPath: "/icons/user.svg", color: COLORS.orange, label: "Salary/Maid" },
  repeat: { iconPath: "/icons/arrows-clockwise.svg", color: COLORS.teal, label: "Subscription" },
  gas_pump: { iconPath: "/icons/gas-pump.svg", color: COLORS.amber, label: "Fuel" },
  bag: { iconPath: "/icons/bag.svg", color: COLORS.red, label: "Shopping" },
  airplane: { iconPath: "/icons/globe.svg", color: COLORS.blue, label: "Travel" },
  phone: { iconPath: "/icons/credit-card.svg", color: COLORS.accent, label: "Recharge" },
  shirt: { iconPath: "/icons/bag.svg", color: COLORS.pink, label: "Clothing" },
  wallet: { iconPath: "/icons/wallet.svg", color: COLORS.accent, label: "Wallet" },
  calendar: { iconPath: "/icons/calendar.svg", color: COLORS.blue, label: "Calendar" },
  bell: { iconPath: "/icons/bell.svg", color: COLORS.amber, label: "Reminder" },
  gift: { iconPath: "/icons/bag.svg", color: COLORS.pink, label: "Gift" },
  camera: { iconPath: "/icons/camera.svg", color: COLORS.purple, label: "Camera" },
  sun: { iconPath: "/icons/sun.svg", color: COLORS.amber, label: "Leisure" },
  water: { iconPath: "/icons/water-drop.svg", color: COLORS.blue, label: "Water" },
  lock: { iconPath: "/icons/lock.svg", color: COLORS.red, label: "Insurance" },
  info: { iconPath: "/icons/info.svg", color: COLORS.teal, label: "Info" },
  users: { iconPath: "/icons/users.svg", color: COLORS.orange, label: "Family" },
  chart: { iconPath: "/icons/chart-bar.svg", color: COLORS.accent, label: "Investment" },
  download: { iconPath: "/icons/download.svg", color: COLORS.purple, label: "Savings" },
  warning: { iconPath: "/icons/warning.svg", color: COLORS.amber, label: "Emergency" },
  dots: { iconPath: "/icons/dots-three.svg", color: COLORS.zinc, label: "Other" },
};

export const SELECTABLE_ICON_KEYS = Object.keys(CATEGORY_ICONS);

export function resolveCategoryIcon(
  emoji: string | null | undefined,
): CategoryIconMeta {
  if (!emoji) return FALLBACK;
  return CATEGORY_ICONS[emoji] ?? FALLBACK;
}

type Size = "sm" | "md" | "lg" | "xl";
const SIZE_PX: Record<Size, { box: number; icon: number; radius: number }> = {
  sm: { box: 28, icon: 14, radius: 8 },
  md: { box: 32, icon: 16, radius: 10 },
  lg: { box: 40, icon: 20, radius: 12 },
  xl: { box: 72, icon: 32, radius: 16 },
};

export interface CategoryIconProps {
  emoji: string | null | undefined;
  size?: Size;
  className?: string;
}

// SVG assets from Flutter use stroke="currentColor". Rendering them as
// <img> would show them in the browser's fallback color. CSS mask-image
// paints them in any color we set on background — the icon shape acts
// as a stencil over the colored background.
export function CategoryIcon({
  emoji,
  size = "md",
  className = "",
}: CategoryIconProps): React.JSX.Element {
  const meta = resolveCategoryIcon(emoji);
  const { box, icon, radius } = SIZE_PX[size];
  return (
    <div
      className={`inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{
        width: box,
        height: box,
        borderRadius: radius,
        backgroundColor: `${meta.color}1a`, // ~10% alpha
        border: `1px solid ${meta.color}33`, // ~20% alpha
      }}
      aria-label={meta.label}
    >
      <span
        style={{
          display: "inline-block",
          width: icon,
          height: icon,
          backgroundColor: meta.color,
          WebkitMaskImage: `url(${meta.iconPath})`,
          maskImage: `url(${meta.iconPath})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
      />
    </div>
  );
}
